/**
 * Display-unit conversion for volumes the API still serves in SCF.
 *
 * Every volume the platform SHOWS is MSCF — the Data screen, its CSV export, and
 * the batch figures. But `calc_results` are filed figures and stay in the unit
 * they were computed in: `total_hub_scf` is SCF on the wire and must remain so,
 * because the RFS4800 workbook reports SCF and the backend's own regression gate
 * asserts those rows are byte-identical across this change.
 *
 * So the conversion happens here, at the edge, on the way to a component. It is
 * an exact Decimal shift, never `Number()` (locked decision #6), and it produces
 * a new figure rather than mutating the parsed response.
 */
import { Decimal } from "@compliance/domain";
import type { Figure } from "../api/types";

const SCF_PER_MSCF = new Decimal(1000);

/**
 * An SCF figure → the same quantity as MSCF.
 *
 * Returns the figure untouched when it is not SCF, so a caller can apply it to
 * an optional figure without first proving the basis.
 */
export function scfFigureAsMscf(figure: Figure | undefined): Figure | undefined {
  if (!figure || figure.basis !== "scf") return figure;
  return {
    value: new Decimal(figure.value).dividedBy(SCF_PER_MSCF).toFixed(),
    basis: "mscf",
  };
}
